import Clear from '@mui/icons-material/Clear';
import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';

import { Driver } from '~hooks/useDrivers/models';
import {
  BasicEquipment,
  BasicEquipmentDefault,
  EquipmentTypeahead,
} from '~hooks/useEquipment/models';
import { EquipmentSelector } from '~pages/Settings/UserManagement/EquipmentSelector';
import { Nullable } from '~types/Nullable';

interface UpdateCompanyShareParams {
  companyShareId: string;
  default_equipment_id: string | null;
}

interface EquipmentColumnHandlers {
  onEditEquipment?: (equipment: BasicEquipment, isInternal: boolean) => void;
  onUpdateCompanyShare: (params: UpdateCompanyShareParams) => Promise<void>;
  onUpdateDriver: (driver: Driver) => Promise<unknown>;
}

interface EquipmentManagementColumnProps {
  driver: Driver;
  canEdit: boolean;
  isUpdating: boolean;
  handlers: EquipmentColumnHandlers;
  companyId?: string;
  managedCompanyId?: string;
  showEditButton?: boolean;
}

export default function EquipmentManagementColumn({
  driver,
  canEdit,
  isUpdating,
  handlers,
  companyId,
  managedCompanyId,
  showEditButton = true,
}: EquipmentManagementColumnProps) {
  const isInternalDriver = !driver.companyShare;
  const noDefaultEquipment = !driver.equipment && !driver.companyShare?.defaultEquipment;

  const handleEditEquipment = () => {
    if (!handlers.onEditEquipment) {
      // TODO: Implement edit equipment functionality
      return;
    }

    const equipmentToEdit = driver.companyShare?.defaultEquipment
      ? (driver.companyShare?.defaultEquipment as unknown as BasicEquipment)
      : (driver.equipment as unknown as BasicEquipment);
    const isEquipmentExternal = !!equipmentToEdit?.companyShare;
    handlers.onEditEquipment(equipmentToEdit, !isEquipmentExternal);
  };

  const handleClearEquipment = async () => {
    if (driver.companyShare) {
      driver.companyShare.defaultEquipment = null;
      await handlers.onUpdateCompanyShare({
        companyShareId: driver.companyShare.id,
        default_equipment_id: null,
      });

      // additional cleanup for managed drivers
      if (driver.managed && driver.equipment) {
        driver.equipment = null;
        await handlers.onUpdateDriver(driver);
      }
    } else if (driver.equipment) {
      driver.equipment = null;
      await handlers.onUpdateDriver(driver);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box
        sx={{
          flex: 3,
          minWidth: 200,
        }}
      >
        {isInternalDriver ? (
          <EquipmentSelector
            defaultEquipment={driver.equipment || null}
            disabled={!canEdit || isUpdating}
            onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
              driver.equipment = equipment as BasicEquipment;
              handlers.onUpdateDriver(driver);
            }}
            clearable={false}
            companyId={companyId}
            managedCompanyId={managedCompanyId}
          />
        ) : (
          <>
            {driver.companyShare && (
              <EquipmentSelector
                defaultEquipment={
                  driver.companyShare.defaultEquipment || driver.equipment
                }
                disabled={!canEdit || isUpdating}
                onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
                  if (equipment) {
                    driver.companyShare!.defaultEquipment =
                      equipment as unknown as BasicEquipmentDefault;

                    handlers.onUpdateCompanyShare({
                      companyShareId: driver.companyShare!.id,
                      default_equipment_id: equipment?.id,
                    });
                  }
                }}
                clearable={false}
                companyId={companyId}
                managedCompanyId={managedCompanyId}
              />
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 100,
        }}
      >
        {((driver.companyShare && driver.companyShare.defaultEquipment) ||
          (!driver.companyShare && driver.equipment)) && (
          <>
            {showEditButton && (
              <Tooltip title={t('administration.equipment.edit_truck')}>
                <IconButton
                  disabled={!canEdit || isUpdating || noDefaultEquipment}
                  onClick={handleEditEquipment}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('administration.equipment.clear_truck')}>
              <IconButton
                disabled={!canEdit || isUpdating || noDefaultEquipment}
                onClick={handleClearEquipment}
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
